import type { CreateConversationMessagePayload } from "#features/conversations/api/useCreateConversationMessageMutation.ts";
import { useConversationMessageCacheHandler } from "#features/conversations/hooks/useConversationMessageCacheHandler.ts";
import { createFedConversationStream, type FedConversationStream } from "#features/conversations/lib/conversationStream.ts";
import { getConversationSourceChannels } from "#features/conversations/hooks/useConversationSources.ts";
import type { ConnectorChannel } from "#features/conversations/types/channels.tsx";
import type { AssistantError } from "#features/conversations/types/assistantError.ts";
import type { ConversationMessageModel } from "#features/conversations/types/ConversationMessageModel.ts";
import { showPendingConnection } from "#features/conversations/lib/connectionRecords.ts";
import { showPendingQuestion } from "#features/conversations/lib/questionRecords.ts";
import { showPendingApproval } from "#features/conversations/lib/toolApprovalRecords.ts";
import { GENERATED_FILE_ACTIVITY_TYPE, isWlaiPersonProfileEvent, isWlaiSourcesEvent, PAGE_ACTION_EXECUTED_RESULT, PAGE_ACTION_TOOL_PREFIX, SKILL_LOADED_ACTIVITY_TYPE } from "#features/conversations/types/customStreamEvents.ts";
import { CONTENT_BLOCKED_RUN_ERROR_CODE, readRunErrorMetadata, readRunFinishedMetadata } from "#features/conversations/types/nativeRunMetadata.ts";
import { type AssistantThinkingStep, type ThinkingStepCode, ThinkingStepCodes, ThinkingStepStates, ThinkingStepStatuses } from "#features/conversations/types/ThinkingStep.ts";
import type { AssistantStatus } from "#features/conversations/utils/assistantStatus.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import { dispatchCustomEvent } from "#lib/events/eventDispatcher.ts";
import type { AssistantStreamEvent } from "#types/events.ts";
import type { PageActionResponse } from "#types/PageActionModel.ts";
import { MixpanelEvent, MixpanelPageActionType } from "#types/tracking.ts";
import type { ConnectedIntegration, ConversationSource, GeneratedFile, StatusActivity } from "#types/types.ts";
import { useQueryClient } from "@tanstack/react-query";
import { type RefObject, useCallback } from "react";

export interface NativeStreamBridge extends FedConversationStream {
    // Resolves once the native agent has applied every frame of this turn. Rejects when the run
    // errored or the agent failed; the Stop button's abort still resolves.
    settled: Promise<void>;
}

export type PageActionOptions = Pick<CreateConversationMessagePayload, "pageActions" | "promptFeature">;

export class StreamError extends Error {
    readonly assistantError?: AssistantError;

    constructor(assistantError?: AssistantError) {
        super(assistantError?.message ?? undefined);
        this.assistantError = assistantError;
    }
}
export class BlockedError extends Error { }
export class AuthError extends Error { }

const getSourceTitles = (sources: ConversationSource[]): string[] => {
    return Array.from(
        new Set(
            sources
                .map(source => source.title?.trim())
                .filter((title): title is string => !!title)
        )
    ).slice(0, 3);
};

const completeThinkingSteps = (message: ConversationMessageModel): ConversationMessageModel => {
    if (!message.thinkingSteps || message.thinkingSteps.length === 0) {
        return message;
    }

    return {
        ...message,
        thinkingSteps: message.thinkingSteps.map(step => (
            step.state === ThinkingStepStates.completed
                ? step
                : { ...step, state: ThinkingStepStates.completed }
        ))
    };
};

interface AppendThinkingStepOptions {
    sourceCount?: number;
    sourceTitles?: string[];
    sourceChannels?: ConnectorChannel[];
    detail?: string;
    title?: string;
    iconKey?: string;
    connectedIntegration?: ConnectedIntegration;
}

const appendThinkingStep = (
    message: ConversationMessageModel,
    code: ThinkingStepCode,
    options: AppendThinkingStepOptions = {}
): ConversationMessageModel => {
    const { sourceCount, sourceTitles, sourceChannels, detail, title, iconKey, connectedIntegration } = options;
    const existingSteps = message.thinkingSteps ?? [];
    const lastStep = existingSteps.at(-1);
    const effectiveSourceCount = sourceCount ?? message.sources.length;
    const effectiveSourceTitles = sourceTitles ?? [];

    // If there's an active step with the same code, sourceCount, and detail, no change needed.
    // The !detail guard handles the edge case where an empty detail is passed — in that case
    // we don't treat it as a meaningful change and skip creating a new step.
    if (
        lastStep &&
        lastStep.state === ThinkingStepStates.active &&
        lastStep.code === code &&
        lastStep.sourceCount === effectiveSourceCount &&
        (!detail || lastStep.detail === detail) &&
        // A skill step is identified by the skill it names, so a different skill starts a new step.
        (code !== ThinkingStepCodes.skillLoaded || lastStep.title === title)
    ) {
        return message;
    }

    // Create a new step when the code, sourceCount, or detail changed

    const completedSteps = existingSteps.map((step, index) => (
        index === existingSteps.length - 1 && step.state === ThinkingStepStates.active
            ? { ...step, state: ThinkingStepStates.completed }
            : step
    ));
    const nextStep: AssistantThinkingStep = {
        id: `${code}-${completedSteps.length + 1}`,
        code,
        state: ThinkingStepStates.active,
        sourceCount: effectiveSourceCount,
        sourceTitles: effectiveSourceTitles
    };

    if (title) {
        nextStep.title = title;
    }

    if (iconKey) {
        nextStep.iconKey = iconKey;
    }

    if (connectedIntegration) {
        nextStep.connectedIntegration = connectedIntegration;
    }

    if (detail) {
        nextStep.detail = detail;
    }

    if (sourceChannels) {
        nextStep.sourceChannels = sourceChannels;
    }

    return {
        ...message,
        thinkingSteps: [
            ...completedSteps,
            nextStep
        ]
    };
};

// core-python's display rule for the domain thinking status; native reasoning arrives unabridged (R6.2).
const THINKING_DETAIL_MAX_CHARS = 2400;

const formatThinkingDetail = (content: string): string => {
    const normalized = content.replace(/\s+/g, " ").trim();

    return normalized.length > THINKING_DETAIL_MAX_CHARS ? `${normalized.slice(0, THINKING_DETAIL_MAX_CHARS).trimEnd()}...` : normalized;
};

export const useConversationMessageStreamHandler = (messageIdRef: RefObject<string>) => {
    const queryClient = useQueryClient();
    const { updateMessage } = useConversationMessageCacheHandler();
    const { trackEvent } = useTracking();

    const appendAssistantToken = useCallback((conversationId: string, messageId: string, text: string) => {
        updateMessage(conversationId, messageId, message => {
            const code = ThinkingStepCodes.assistantMessage;
            const sourceCount = message.sources.length;

            return {
                ...appendThinkingStep(message, code, { sourceCount }),
                content: `${message.content}${text}`
            };
        });
    }, [updateMessage]);

    // Sources arrive only as the native CUSTOM `wlai_sources` event. Accumulate the message's sources and keep a
    // single "Collected N sources" thinking step, updated in place so a later batch never reads as a second search.
    const appendSources = useCallback((conversationId: string, messageId: string, sources: ConversationSource[]) => {
        updateMessage(conversationId, messageId, message => {
            const code = ThinkingStepCodes.sources;
            const combinedSources = [...message.sources, ...sources];
            const sourceStep = {
                sourceCount: combinedSources.length,
                sourceTitles: getSourceTitles(combinedSources),
                sourceChannels: getConversationSourceChannels(combinedSources)
            };
            const updatedMessage = { ...message, sources: combinedSources };

            if (message.thinkingSteps?.some(step => step.code === code)) {
                return {
                    ...updatedMessage,
                    thinkingSteps: message.thinkingSteps.map(step => step.code === code ? { ...step, ...sourceStep } : step)
                };
            }

            return appendThinkingStep(updatedMessage, code, sourceStep);
        });
    }, [updateMessage]);

    const appendStatusStep = useCallback((conversationId: string, messageId: string, status: string, step: Pick<AppendThinkingStepOptions, "detail" | "title" | "iconKey" | "connectedIntegration">) => {
        updateMessage(conversationId, messageId, message => {
            const updatedMessage = { ...message, assistantStatus: status as AssistantStatus };
            if (!ThinkingStepStatuses.has(status as AssistantStatus)) {
                return updatedMessage;
            }

            return appendThinkingStep(updatedMessage, status as ThinkingStepCode, { sourceCount: updatedMessage.sources.length, ...step });
        });
    }, [updateMessage]);

    // A skill step is not a status, so the message's assistantStatus stays as it was.
    const appendSkillStep = useCallback((conversationId: string, messageId: string, title: string | undefined) => {
        updateMessage(conversationId, messageId, message => (
            appendThinkingStep(message, ThinkingStepCodes.skillLoaded, { sourceCount: message.sources.length, title })
        ));
    }, [updateMessage]);

    // Applied both when the blocked RUN_ERROR arrives and when the failed turn settles, so it must not
    // change a message that already shows as blocked.
    const markBlocked = useCallback((conversationId: string, messageId: string) => {
        updateMessage(conversationId, messageId, message => (
            message.assistantStatus === "blocked" ? message : completeThinkingSteps({ ...message, assistantStatus: "blocked" })
        ));
    }, [updateMessage]);

    const markMemoryUpdated = useCallback((conversationId: string, messageId: string) => {
        updateMessage(conversationId, messageId, message => ({
            ...message,
            isMemoryUpdated: true
        }));
        trackEvent(MixpanelEvent.AssistantMemoryUpdated, {
            Location: "Conversation"
        });
        queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetPersonalization() });
    }, [queryClient, trackEvent, updateMessage]);

    const appendGeneratedFile = useCallback((conversationId: string, messageId: string, generatedFile: GeneratedFile) => {
        updateMessage(conversationId, messageId, message => {
            const generatedFiles = message.generatedFiles ?? [];
            if (generatedFiles.some(file => file.downloadUrl === generatedFile.downloadUrl)) {
                return message;
            }

            return {
                ...message,
                generatedFiles: [...generatedFiles, generatedFile]
            };
        });
    }, [updateMessage]);

    const applyPageActionResponse = useCallback((conversationId: string, messageId: string, response: PageActionResponse, { pageActions, promptFeature }: PageActionOptions) => {
        const pageAction = pageActions?.find(({ action }) => action === response.action);

        if (pageAction?.onResponse) {
            pageAction.onResponse(response);

            trackEvent(MixpanelEvent.AssistantPageActionTriggered, {
                "Page Action": response.action,
                "Feature": promptFeature,
                "Type": MixpanelPageActionType.AutoApplied
            });
        }

        updateMessage(conversationId, messageId, message => ({
            ...message,
            pageActionResponse: response
        }));
    }, [trackEvent, updateMessage]);

    // Runs a ConversationStreamAgent on the turn's stream (fed via the returned controller) and materializes
    // the native AG-UI vocabulary from it. It is the stream's only reader: it forwards every event to
    // integrators on the window bus, and reads assistant text (TEXT_MESSAGE_CONTENT), sources (CUSTOM
    // wlai_sources), the person profile (CUSTOM wlai_person_profile), statuses, loaded skills and generated files (ACTIVITY_SNAPSHOT), reasoning
    // (REASONING_MESSAGE_*), page-action responses (a page_action_* call's successful TOOL_CALL_RESULT), the
    // run's end (RUN_FINISHED metadata), its errors (RUN_ERROR), its pending tool approvals (the
    // RUN_FINISHED interrupt; the native Interrupt is lossy, so name/args come from the tool call the SDK
    // built from TOOL_CALL_*) and its question (the `wlai_question` interrupt).
    //
    // The returned `settled` settles once the agent has applied every frame of the turn. The caller awaits
    // it before handleClose, which renames the optimistic message id: a native update applied after the
    // rename would target an id that no longer exists.
    const startNativeStreamAgent = useCallback((conversationId: string, messageId: string, pageActionOptions: PageActionOptions = {}): NativeStreamBridge => {
        const stream = createFedConversationStream();
        // Lazy-load the agent (and, transitively, @ag-ui/client) so the heavy SDK is code-split out of
        // the app's boot bundle — it loads only when a turn actually streams. Frames that arrive before
        // the agent subscribes are buffered by the fed stream, so nothing is lost during the import.
        const settled = (async () => {
            let runError: BlockedError | StreamError | undefined;
            try {
                const [{ ConversationStreamAgent }, { buildPendingToolCalls }, { buildPendingQuestion }, { buildPendingConnection }] = await Promise.all([
                    import("#features/conversations/lib/conversationStreamAgent.ts"),
                    import("#features/conversations/lib/buildPendingToolCalls.ts"),
                    import("#features/conversations/lib/buildPendingQuestion.ts"),
                    import("#features/conversations/lib/buildPendingConnection.ts")
                ]);
                const agent = new ConversationStreamAgent(stream.connect, conversationId);
                agent.subscribe({
                    // Integrators read every native event off the window bus (`@workleap-ai/client`'s
                    // waiAssistantStreamEvent). The agent is the stream's only reader, so it forwards them.
                    onEvent: ({ event }) => {
                        dispatchCustomEvent("waiAssistantStreamEvent", event as unknown as AssistantStreamEvent);
                    },
                    onTextMessageContentEvent: ({ event }) => {
                        appendAssistantToken(conversationId, messageId, event.delta);
                    },
                    onCustomEvent: ({ event }) => {
                        if (isWlaiSourcesEvent(event)) {
                            appendSources(conversationId, messageId, event.value.sources);
                        } else if (isWlaiPersonProfileEvent(event)) {
                            updateMessage(conversationId, messageId, message => ({ ...message, personProfile: event.value.profile }));
                        }
                        // An unrecognised CUSTOM name is ignored and the run continues (R2.4).
                    },
                    onToolCallResultEvent: ({ event, messages }) => {
                        if (event.content !== PAGE_ACTION_EXECUTED_RESULT) {
                            return;
                        }
                        // The arguments come from the tool call the SDK assembled from TOOL_CALL_START/ARGS.
                        const toolCall = messages
                            .flatMap(message => (message.role === "assistant" ? message.toolCalls ?? [] : []))
                            .find(call => call.id === event.toolCallId);
                        if (!toolCall?.function.name.startsWith(PAGE_ACTION_TOOL_PREFIX)) {
                            return;
                        }
                        applyPageActionResponse(conversationId, messageId, {
                            action: toolCall.function.name.slice(PAGE_ACTION_TOOL_PREFIX.length),
                            content: toolCall.function.arguments || "{}"
                        }, pageActionOptions);
                    },
                    onActivitySnapshotEvent: ({ event }) => {
                        if (event.activityType === GENERATED_FILE_ACTIVITY_TYPE) {
                            appendGeneratedFile(conversationId, messageId, event.content as GeneratedFile);

                            return;
                        }
                        const { title, detail, iconKey, connectedIntegration } = (event.content ?? {}) as StatusActivity;
                        if (event.activityType === SKILL_LOADED_ACTIVITY_TYPE) {
                            appendSkillStep(conversationId, messageId, title ?? undefined);

                            return;
                        }
                        // An activity type this client does not know renders no step; the run continues (R2.4).
                        appendStatusStep(conversationId, messageId, event.activityType, {
                            title: title ?? undefined,
                            detail: detail ?? undefined,
                            iconKey: iconKey ?? undefined,
                            connectedIntegration: (connectedIntegration ?? undefined) as ConnectedIntegration | undefined
                        });
                    },
                    onReasoningMessageEndEvent: ({ reasoningMessageBuffer }) => {
                        const detail = formatThinkingDetail(reasoningMessageBuffer);
                        if (detail) {
                            appendStatusStep(conversationId, messageId, ThinkingStepCodes.thinking, { detail });
                        }
                    },
                    onRunFinishedEvent: ({ event }) => {
                        // The answer is done; core closes the stream after its own bookkeeping.
                        updateMessage(conversationId, messageId, message => ({ ...message, assistantStatus: "end_of_stream" }));
                        const { conversationMessageId, conversationTitle, userMemoryUpdated } = readRunFinishedMetadata(event.metadata);
                        if (conversationMessageId) {
                            messageIdRef.current = conversationMessageId;
                        }
                        if (conversationTitle) {
                            queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetConversations() });
                        }
                        if (userMemoryUpdated) {
                            markMemoryUpdated(conversationId, messageId);
                        }
                    },
                    onRunErrorEvent: ({ event }) => {
                        if (event.code === CONTENT_BLOCKED_RUN_ERROR_CODE) {
                            runError = new BlockedError();
                            // Core keeps the stream open to resolve the title; show the block now.
                            markBlocked(conversationId, messageId);

                            return;
                        }
                        runError = new StreamError({ code: event.code ?? "unknown", message: event.message, tool: readRunErrorMetadata(event.metadata).tool ?? null });
                    }
                });
                // pendingInterrupts is populated once the run resolves, so read it after runAgent (not from
                // an onRunFinishedEvent subscriber, which fires before the interrupt outcome is applied).
                await agent.runAgent();
                // A run can also end on an interrupt that is not an approval; it builds no call and must not pin an empty card.
                const approvalCalls = buildPendingToolCalls(agent.pendingInterrupts, agent.messages);
                if (approvalCalls.length > 0) {
                    // The bridge settles before handleClose renames the message, so the optimistic id is still its
                    // key here, and the rename carries the approval record with it.
                    showPendingApproval(queryClient, conversationId, messageId, approvalCalls);
                }
                // A run pauses on one kind of interrupt: approvals, one question or one connection request. The rename carries
                // the question and connection records too.
                const question = buildPendingQuestion(agent.pendingInterrupts);
                if (question) {
                    showPendingQuestion(queryClient, conversationId, messageId, question, pageActionOptions.promptFeature);
                }
                const connection = buildPendingConnection(agent.pendingInterrupts);
                if (connection) {
                    showPendingConnection(queryClient, conversationId, messageId, connection);
                }
            } catch (error) {
                if (!runError) {
                    if (error instanceof DOMException && error.name === "AbortError") {
                        return; // the Stop button: not a failure
                    }
                    // Text is native-only, so a failed agent (a chunk-load failure on a stale client) must
                    // fail the turn visibly rather than leave a silently empty answer.
                    throw new StreamError();
                }
            }
            if (runError) {
                throw runError;
            }
        })();

        return { ...stream, settled };
    }, [appendAssistantToken, appendGeneratedFile, appendSkillStep, appendSources, appendStatusStep, applyPageActionResponse, markBlocked, markMemoryUpdated, messageIdRef, queryClient, updateMessage]);

    const handleOpen = useCallback(async (response: Response) => {
        if (response.status === 401) {
            throw new AuthError();
        }
        // A rejected request has no event stream; reading its body would settle the turn as an empty reply.
        if (!response.ok) {
            throw new Error(`createConversationMessage failed with HTTP ${response.status}`);
        }
    }, []);

    const handleClose = useCallback((conversationId: string, messageId: string) => {
        const savedMessageId = messageIdRef.current;
        const id = savedMessageId || messageId;
        updateMessage(conversationId, messageId, message => completeThinkingSteps({
            ...message,
            id,
            assistantStatus: "idle",
            // A retried turn closes again on the same message, so a saved close clears an earlier unsaved one.
            isUnsaved: !savedMessageId
        }));
    }, [messageIdRef, updateMessage]);

    const handleError = useCallback((conversationId: string, messageId: string, error: unknown) => {
        if (error instanceof BlockedError) {
            markBlocked(conversationId, messageId);
        } else if (error instanceof AuthError) {
            updateMessage(conversationId, messageId, message => completeThinkingSteps({
                ...message,
                assistantStatus: "unauthenticated"
            }));
        } else {
            const assistantError = error instanceof StreamError ? error.assistantError : undefined;
            updateMessage(conversationId, messageId, message => completeThinkingSteps({
                ...message,
                assistantStatus: "error",
                assistantError: assistantError ?? null
            }));
        }
    }, [markBlocked, updateMessage]);

    return {
        handleOpen,
        handleClose,
        handleError,
        startNativeStreamAgent
    };
};
